import React from 'react';
import { Box, Paper, Avatar, Typography } from '@mui/material';
import { SmartToy, Person } from '@mui/icons-material';
import Text from '../atoms/Text';

export interface MessageBubbleProps {
  content: string;
  sender: 'user' | 'agent';
  timestamp: Date;
  avatar?: string;
  userName?: string;
  isTyping?: boolean;
  isHtml?: boolean;
  isTemporary?: boolean;
  sx?: any;
}

const MessageBubble = ({
  content,
  sender,
  timestamp,
  avatar,
  userName,
  isTyping = false,
  isHtml = false,
  isTemporary = false,
  sx,
}: MessageBubbleProps) => {
  const isUser = sender === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
        alignItems: 'flex-start',
        gap: 1,
        ...sx,
      }}
    >
      {/* Avatar for agent messages */}
      {!isUser && (
        <Avatar
          src={avatar}
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'primary.main',
            order: 0,
          }}
        >
          <SmartToy fontSize="small" />
        </Avatar>
      )}

      {/* Message Content */}
      <Box
        sx={{
          maxWidth: '70%',
          order: isUser ? 1 : 1,
        }}
      >
        {/* Sender name for agent messages */}
        {!isUser && userName && (
          <Typography
            variant="caption"
            sx={{ 
              ml: 1, 
              mb: 0.5,
              fontFamily: 'var(--font-body)',
              color: 'text.secondary',
              fontSize: 12,
              fontWeight: 500
            }}
          >
            {userName}
          </Typography>
        )}

        {/* Message bubble */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            bgcolor: isUser 
              ? 'linear-gradient(45deg, #667eea, #764ba2)' 
              : isTemporary 
              ? 'rgba(255, 243, 224, 0.95)' 
              : 'rgba(255,255,255,0.95)',
            color: isUser ? '#fff' : '#333',
            borderRadius: isUser 
              ? '20px 20px 6px 20px' 
              : '20px 20px 20px 6px',
            border: isUser ? 'none' : isTemporary ? '1px solid rgba(255, 152, 0, 0.3)' : '1px solid rgba(255,255,255,0.3)',
            position: 'relative',
            boxShadow: '0 4px 15px rgba(0,0,0,0.15)',
            backdropFilter: !isUser ? 'blur(10px)' : 'none',
            background: isUser 
              ? 'linear-gradient(45deg, #667eea, #764ba2)' 
              : isTemporary 
              ? 'rgba(255, 243, 224, 0.95)' 
              : 'rgba(255,255,255,0.95)',
            animation: isTemporary ? 'fadeInOut 3s ease-in-out' : 'none',
            '@keyframes fadeInOut': {
              '0%': { opacity: 0, transform: 'translateY(-10px)' },
              '10%': { opacity: 1, transform: 'translateY(0)' },
              '90%': { opacity: 1, transform: 'translateY(0)' },
              '100%': { opacity: 0, transform: 'translateY(-10px)' },
            },
          }}
        >
          {isHtml ? (
            <Box
              sx={{
                wordBreak: 'break-word',
                color: 'inherit',
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                lineHeight: 1.6,
                '& p': { 
                  margin: '0 0 12px 0',
                  '&:last-child': { marginBottom: 0 }
                },
                '& strong': { fontWeight: 700 },
                '& em': { fontStyle: 'italic' },
                '& ul, & ol': { 
                  marginLeft: '20px',
                  marginBottom: '12px',
                  paddingLeft: '0',
                },
                '& li': { 
                  marginBottom: '6px',
                  lineHeight: 1.5,
                },
                '& h1, & h2, & h3': {
                  marginTop: '12px',
                  marginBottom: '8px',
                  fontWeight: 700,
                },
              }}
              dangerouslySetInnerHTML={{ __html: content }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                color: 'inherit',
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              {content}
            </Typography>
          )}

          {/* Typing indicator */}
          {isTyping && (
            <Box
              sx={{
                display: 'flex',
                gap: 0.5,
                mt: 1,
                alignItems: 'center',
              }}
            >
              {[1, 2, 3].map((dot) => (
                <Box
                  key={dot}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: isUser ? 'primary.contrastText' : 'text.secondary',
                    animation: 'typing 1.4s infinite ease-in-out',
                    animationDelay: `${dot * 0.2}s`,
                    '@keyframes typing': {
                      '0%, 60%, 100%': {
                        opacity: 0.3,
                      },
                      '30%': {
                        opacity: 1,
                      },
                    },
                  }}
                />
              ))}
            </Box>
          )}
        </Paper>

        {/* Timestamp */}
        <Box
          sx={{
            mt: 0.5,
            display: 'flex',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          <Text
            content={timestamp.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
            variant="caption"
            color="textSecondary"
            sx={{ px: 1 }}
          />
        </Box>
      </Box>

      {/* Avatar for user messages */}
      {isUser && (
        <Avatar
          src={avatar}
          sx={{
            width: 32,
            height: 32,
            bgcolor: 'secondary.main',
            order: 2,
          }}
        >
          {userName ? userName.charAt(0).toUpperCase() : <Person fontSize="small" />}
        </Avatar>
      )}
    </Box>
  );
};

export default MessageBubble; 